<template>
    <div class="space-y-8">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div class="space-y-4 border p-4">
                <div class="border-b-2 pb-2 text-xl text-gray-700">
                    {{ t('purchaseContract.participants') }}
                </div>
                <ul class="list-disc pl-5">
                    <template
                        v-for="(
                            specification, index
                        ) in contractData.enrollmentSpecifications"
                        :key="index"
                    >
                        <template v-if="specification.count > 0">
                            <template v-if="specification.enrollments.length">
                                <li
                                    v-for="(
                                        enrollment, enrollmentIndex
                                    ) in specification.enrollments"
                                    :key="enrollmentIndex"
                                >
                                    {{ enrollment.customer.commaName }}
                                    ({{ enrollment.enrollmentType.singular }})
                                </li>
                            </template>
                            <li v-else>
                                {{ specification.count }}x
                                {{ specification.enrollmentType.singular }}
                            </li>
                        </template>
                    </template>
                </ul>
            </div>

            <div
                v-if="
                    planRequiresFullAddress ||
                    planRequiresAddressCountryAndPostal ||
                    contractCustomer !== null
                "
                class="space-y-4 border p-4"
            >
                <template v-if="contractCustomer !== null">
                    <div class="border-b-2 pb-2 text-xl">
                        {{ t('purchaseContract.customerInformation') }}
                    </div>
                    <p class="whitespace-pre text-lg">
                        {{ contractCustomer.firstName
                        }}<span v-if="contractCustomer.middleName !== ''"
                            >&nbsp;{{ contractCustomer.middleName }}</span
                        >
                        {{ contractCustomer.lastName }}
                        <br />
                        <span class="text-sm">{{
                            formatDateOfBirth(contractCustomer.dateOfBirth)
                        }}</span>
                        <br />
                        <span class="mt-1">{{ contractCustomer.email }}</span>
                        <template v-if="contractCustomer.mobilePhone !== ''">
                            <br />
                            <span class="text-sm">{{
                                contractCustomer.mobilePhone
                            }}</span>
                        </template>
                    </p>
                </template>

                <template
                    v-if="
                        planRequiresFullAddress ||
                        planRequiresAddressCountryAndPostal
                    "
                >
                    <div class="border-b-2 pb-2 text-xl">
                        {{ t('address.address') }}
                    </div>
                    <div v-if="planRequiresAddressCountryAndPostal">
                        <div>
                            {{
                                t('address.displayCountry', {
                                    country: contractAddress.country
                                })
                            }}
                        </div>
                        <div>
                            {{
                                t('address.displayPostal', {
                                    postal: contractAddress.postal
                                })
                            }}
                        </div>
                    </div>
                    <p
                        v-if="planRequiresFullAddress"
                        class="whitespace-pre text-lg"
                    >
                        {{ contractAddress.formattedBlock }}
                    </p>
                </template>
            </div>
        </div>
        <div
            v-if="contractData.properties.length !== 0"
            class="space-y-4 border p-4"
        >
            <div
                v-for="property in contractData.properties"
                :key="property.id"
            >
                <contract-property-display :property="property" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const ParticipantDetailsCompletedContractFragment = graphql(`
    fragment ParticipantDetailsCompletedContractFragment on PlanContract {
        enrollmentSpecifications {
            enrollmentType {
                singular
            }
            count
            enrollments {
                customer {
                    id
                    commaName
                }
                enrollmentType {
                    singular
                }
            }
        }
        properties {
            id
            ...ContractPropertyDisplayFragment
        }
        planType
        plan {
            terms {
                addressRequirement
            }
        }
        terms {
            address {
                formattedBlock
                country
                postal
            }
            customer {
                firstName
                middleName
                lastName
                dateOfBirth
                email
                mobilePhone
            }
        }
    }
`)
</script>

<script setup lang="ts">
import ContractPropertyDisplay from '~/components/ContractDetail/ContractPropertyDisplay.vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { computed } from 'vue'
import { differenceInYears } from 'date-fns'
import {
    formatTimelessDate,
    now,
    parseLocal,
    parseTimeless
} from '~/composables/support/localization'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    contract: FragmentType<typeof ParticipantDetailsCompletedContractFragment>
}>()

const { t } = useI18n({ useScope: 'global' })
const contractData = computed(() =>
    getFragmentData(ParticipantDetailsCompletedContractFragment, props.contract)
)

const planRequiresAddressCountryAndPostal = computed(() => {
    return (
        contractData.value.planType === 'AGREEMENT' &&
        contractData.value.plan.terms.addressRequirement ===
            'COUNTRY_AND_POSTAL_CODE'
    )
})

const planRequiresFullAddress = computed(() => {
    return (
        contractData.value.planType === 'AGREEMENT' &&
        contractData.value.plan.terms.addressRequirement === 'FULL'
    )
})

const contractAddress = computed(() => {
    if (contractData.value.terms.address === null) {
        throw new Error()
    }

    return contractData.value.terms.address
})

const contractCustomer = computed(() => {
    if (contractData.value.terms.customer === null) {
        return null
    }
    return contractData.value.terms.customer
})

const activeFacility = useActiveFacility()
function formatDateOfBirth(dateOfBirth: string) {
    // TODO: Localize
    return (
        formatTimelessDate(parseTimeless(dateOfBirth)) +
        ' - ' +
        t('customerInformation.age', {
            age: differenceInYears(
                now(activeFacility.value.timezone),
                parseLocal(dateOfBirth)
            )
        })
    )
}
</script>
